import { useState, useEffect, useCallback, useRef } from 'react';
import { SWEEPSTAKES_MOCK_DATA } from '../core/Data/SweepstakesMockData';

// Custom hook for executing async functions in useEffect with cleanup
const useAsyncEffect = (asyncFunction, dependencies) => {
  useEffect(() => {
    let isCancelled = false;

    const executeAsync = async () => {
      if (!isCancelled) {
        await asyncFunction();
      }
    };

    executeAsync();

    return () => {
      isCancelled = true;
    };
  }, dependencies);
};

export const useFetchSweepstakes = ({ providedData, userOptInStatus = 'notOptedIn' } = {}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fetchSweepstakesData = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);

    try {
      // Simulate network delay - use shorter delay in test environment
      const delay = process.env.NODE_ENV === 'test' ? 100 : 1500;
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });

      // Use provided data if available, otherwise get mock data based on user status
      let responseData;
      if (providedData) {
        responseData = providedData;
      } else {
        switch (userOptInStatus) {
        case 'notOptedIn':
          responseData = SWEEPSTAKES_MOCK_DATA.notOptedIn;
          break;
        case 'partiallyCompleted':
          responseData = SWEEPSTAKES_MOCK_DATA.partiallyCompleted;
          break;
        case 'fullyCompleted':
          responseData = SWEEPSTAKES_MOCK_DATA.fullyCompleted;
          break;
        default:
          responseData = SWEEPSTAKES_MOCK_DATA.notOptedIn;
        }
      }

      setData(responseData);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [providedData, userOptInStatus]);

  useAsyncEffect(fetchSweepstakesData, [fetchSweepstakesData]);

  const refresh = useCallback(() => {
    fetchSweepstakesData();
  }, [fetchSweepstakesData]);

  return {
    data,
    isLoading,
    hasError,
    refresh,
  };
};

export const useFetchSweepstakesAccept = ({ forceError = false } = {}) => {
  const [acceptData, setAcceptData] = useState(null);
  const [isLoadingAccept, setIsLoadingAccept] = useState(false);
  const [acceptError, setAcceptError] = useState(false);

  const acceptSweepstakes = useCallback(async () => {
    setIsLoadingAccept(true);
    setAcceptError(false);

    try {
      // Simulate accepting sweepstakes
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          if (forceError) {
            reject(new Error('Simulated network error for testing'));
          } else {
            resolve();
          }
        }, 1000);
      });

      const responseData = {
        success: true,
        message: 'Successfully opted into sweepstakes',
      };

      setAcceptData(responseData);
      return responseData;
    } catch (error) {
      setAcceptError(true);
      const errorResponse = { success: false, error: error.message || 'Network error occurred' };
      setAcceptData(errorResponse);
      return errorResponse;
    } finally {
      setIsLoadingAccept(false);
    }
  }, [forceError]);

  return {
    acceptData,
    isLoadingAccept,
    acceptError,
    acceptSweepstakes,
  };
};

export const useUpdateSweepstakes = (baseData) => {
  const [localDataUpdates, setLocalDataUpdates] = useState(null);
  const baseDataRef = useRef(baseData);

  // Keep ref in sync with baseData
  useEffect(() => {
    baseDataRef.current = baseData;
  }, [baseData]);

  // Apply local updates to base data
  const effectiveData = localDataUpdates ? { ...baseData, ...localDataUpdates } : baseData;

  const stepUpdater = (step, updates) => {
    const stepUpdate = updates.steps.find((updateStep) => updateStep.id === step.id);
    return stepUpdate ? { ...step, ...stepUpdate } : step;
  };

  // Function to update sweepstakes data at runtime
  const updateSweepstakesData = useCallback((updates) => {
    setLocalDataUpdates((prevUpdates) => {
      const currentBaseData = baseDataRef.current;
      const currentData = prevUpdates ? { ...currentBaseData, ...prevUpdates } : currentBaseData;
      const updatedData = { ...currentData };
      const processStep = (step) => stepUpdater(step, updates);

      // Apply all updates dynamically
      Object.keys(updates).forEach((key) => {
        if (key === 'completedSteps') {
          // Special handling for completedSteps to ensure it only increases
          updatedData.completedSteps = Math.max(
            updatedData.completedSteps || 0,
            updates.completedSteps
          );
        } else if (key === 'steps' && updates.steps && updatedData.steps) {
          // Special handling for steps array
          updatedData.steps = updatedData.steps.map(processStep);
        } else {
          // Direct assignment for all other fields (ctaTitle, isOptedIn, status, etc.)
          updatedData[key] = updates[key];
        }
      });

      // Return only the updates (delta from base data)
      const deltaUpdates = {};
      Object.keys(updatedData).forEach((key) => {
        if (JSON.stringify(updatedData[key]) !== JSON.stringify(currentBaseData?.[key])) {
          deltaUpdates[key] = updatedData[key];
        }
      });

      return Object.keys(deltaUpdates).length > 0 ? deltaUpdates : null;
    });
  }, []);

  // Reset updates when base data changes
  useEffect(() => {
    setLocalDataUpdates(null);
  }, [baseData]);

  return {
    effectiveData,
    updateSweepstakesData
  };
};